<script setup>
import { ref, nextTick, watch, computed } from 'vue';
import MessageBubble from './MessageBubble.vue';
import TypingIndicator from './TypingIndicator.vue';
import { ChevronDown } from '@lucide/vue';
import { useTranslations } from '../../composables/useTranslations.js';
import { inject } from 'vue';

const props = defineProps({
    messages: { type: Array, default: () => [] },
    streaming: { type: Boolean, default: false },
    status: { type: String, default: 'idle' },
});

const config = inject('aia.config');
const { t } = useTranslations(config?.locale);

const scrollRef = ref(null);
const pinnedToBottom = ref(true);

function handleScroll() {
    if (!scrollRef.value) return;
    const el = scrollRef.value;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottom.value = distanceFromBottom < 32;
}

function scrollToBottom(behavior = 'smooth') {
    nextTick(() => {
        if (scrollRef.value) {
            scrollRef.value.scrollTo({ top: scrollRef.value.scrollHeight, behavior });
        }
    });
}

watch(
    () => [props.messages.length, props.messages[props.messages.length - 1]?.content],
    () => {
        if (pinnedToBottom.value) scrollToBottom();
    }
);

// Index of the trailing assistant turn that has no content yet (the
// pre-first-token placeholder). We hide its empty bubble and show only the
// TypingIndicator while waiting, so there's a single loading state.
const placeholderIndex = computed(() => {
    if (!props.streaming) return -1;
    const lastIndex = props.messages.length - 1;
    const last = props.messages[lastIndex];
    return last?.role === 'assistant' && !last.content ? lastIndex : -1;
});

const showTypingIndicator = computed(() => placeholderIndex.value !== -1);
</script>

<template>
    <div
        ref="scrollRef"
        class="relative flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-3"
        @scroll="handleScroll"
    >
        <template v-for="(msg, i) in messages" :key="i">
            <MessageBubble
                v-if="i !== placeholderIndex"
                :role="msg.role"
                :content="msg.content"
                :streaming="!!msg.streaming"
                :error="!!msg.error"
                :error-key="msg.errorKey || ''"
            />
        </template>
        <TypingIndicator v-if="showTypingIndicator" />

        <Transition name="aia-pill">
            <button
                v-if="!pinnedToBottom"
                type="button"
                class="sticky bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-[var(--primary-foreground)] shadow-lg"
                @click="() => { pinnedToBottom = true; scrollToBottom(); }"
            >
                <ChevronDown :size="14" />
                {{ t('states.scrollToBottom') }}
            </button>
        </Transition>
    </div>
</template>

<style scoped>
.aia-pill-enter-active, .aia-pill-leave-active { transition: opacity 200ms, transform 200ms; }
.aia-pill-enter-from, .aia-pill-leave-to { opacity: 0; transform: translate(-50%, 8px); }
</style>
